import {
  Dialog,
  InvalidInput,
  type OwnPlace,
  type PlaceTag,
  type PlaceTagInput,
} from '@trippath/shared';
import { useId, useRef, useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';

/** The colours a tag may have: the mockup's, each readable as a ring on either map. */
export const TAG_COLOURS = [
  '#dc2626',
  '#ea580c',
  '#ca8a04',
  '#16a34a',
  '#0891b2',
  '#2563eb',
  '#7c3aed',
  '#db2777',
  '#92400e',
  '#475569',
];

/** A tag made or changed: its name, its emoji and its colour. */
export function TagDialog({
  tag,
  onSave,
  onRemove,
  onClose,
}: {
  tag: PlaceTag | null;
  onSave: (input: PlaceTagInput) => Promise<void>;
  /** Lets go of the tag changed. */
  onRemove: (id: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const emojiId = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(tag?.name ?? '');
  const [emoji, setEmoji] = useState(tag?.emoji ?? '📍');
  const [colour, setColour] = useState(tag?.colour ?? '#2563eb');
  const [taken, setTaken] = useState(false);
  const submit = (event: SyntheticEvent) => {
    event.preventDefault();
    if (name.trim() === '' || emoji.trim() === '') return;
    onSave({ name: name.trim(), emoji: emoji.trim(), colour }).catch((error: unknown) => {
      if (error instanceof InvalidInput) setTaken(true);
    });
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={t(tag === null ? 'myPlaces.tagTitle' : 'myPlaces.tagEditTitle')}
      initialFocus={first}
    >
      <form className="form" onSubmit={submit} noValidate>
        <div className="field">
          <label className="field-label" htmlFor={nameId} data-required>
            {t('myPlaces.tagName')}
          </label>
          <input
            ref={first}
            id={nameId}
            className="text-input"
            type="text"
            maxLength={40}
            value={name}
            aria-invalid={taken}
            aria-describedby={taken ? `${nameId}-error` : undefined}
            onChange={(event) => {
              setName(event.target.value);
              setTaken(false);
            }}
          />
          {taken && (
            <p className="field-error" id={`${nameId}-error`}>
              {t('myPlaces.taken')}
            </p>
          )}
        </div>
        <div className="field">
          <label className="field-label" htmlFor={emojiId} data-required>
            {t('myPlaces.emoji')}
          </label>
          <input
            id={emojiId}
            className="text-input places-emoji-input"
            type="text"
            maxLength={8}
            value={emoji}
            onChange={(event) => {
              setEmoji(event.target.value);
            }}
          />
        </div>
        <fieldset className="places-colours">
          <legend className="field-label">{t('myPlaces.colour')}</legend>
          {TAG_COLOURS.map((one) => (
            <label key={one} className="places-colour">
              <input
                type="radio"
                name="colour"
                value={one}
                checked={colour === one}
                aria-label={one}
                onChange={() => {
                  setColour(one);
                }}
              />
              <span style={{ background: one }} />
            </label>
          ))}
        </fieldset>
        <div className="dialog-actions">
          {tag !== null && (
            <button
              type="button"
              className="button button-danger places-grow"
              onClick={() => {
                onRemove(tag.id);
              }}
            >
              {t('myPlaces.removeTag')}
            </button>
          )}
          <button type="button" className="button" onClick={onClose}>
            {t('myPlaces.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('myPlaces.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Which of one's tags a place carries. */
export function TagsDialog({
  place,
  tags,
  onSave,
  onClose,
}: {
  place: OwnPlace;
  tags: PlaceTag[];
  onSave: (tags: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState(place.tags);
  return (
    <Dialog open onClose={onClose} title={t('myPlaces.tagsOf', { name: place.name })}>
      <form
        className="form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(chosen);
        }}
      >
        <fieldset className="places-tag-choice">
          <legend className="visually-hidden">{t('myPlaces.tags')}</legend>
          {tags.map((tag) => (
            <label key={tag.id} className="check-row">
              <input
                type="checkbox"
                checked={chosen.includes(tag.id)}
                onChange={() => {
                  setChosen(
                    chosen.includes(tag.id)
                      ? chosen.filter((one) => one !== tag.id)
                      : [...chosen, tag.id],
                  );
                }}
              />
              <span className="places-dot" style={{ background: tag.colour }} />
              {tag.emoji} {tag.name}
            </label>
          ))}
        </fieldset>
        <div className="dialog-actions">
          <button type="button" className="button" onClick={onClose}>
            {t('myPlaces.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('myPlaces.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** A place's name and notes changed, a photo let go of, or the place itself. */
export function EditDialog({
  place,
  onSave,
  onRemovePhoto,
  onRemove,
  onClose,
}: {
  place: OwnPlace;
  onSave: (name: string, note: string | null) => void;
  onRemovePhoto: (photo: string) => void;
  onRemove: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const nameId = useId();
  const noteId = useId();
  const first = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(place.name);
  const [note, setNote] = useState(place.note ?? '');
  return (
    <Dialog open onClose={onClose} title={t('myPlaces.editTitle')} initialFocus={first}>
      <form
        className="form"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === '') return;
          onSave(name.trim(), note.trim() === '' ? null : note.trim());
        }}
      >
        <div className="field">
          <label className="field-label" htmlFor={nameId} data-required>
            {t('myPlaces.name')}
          </label>
          <input
            ref={first}
            id={nameId}
            className="text-input"
            type="text"
            maxLength={100}
            value={name}
            aria-invalid={name.trim() === ''}
            onChange={(event) => {
              setName(event.target.value);
            }}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor={noteId}>
            {t('myPlaces.note')}
          </label>
          <textarea
            id={noteId}
            className="text-input"
            rows={4}
            maxLength={2000}
            value={note}
            onChange={(event) => {
              setNote(event.target.value);
            }}
          />
        </div>
        {place.photos.length > 0 && (
          <ul className="places-photo-edit">
            {place.photos.map((photo, at) => (
              <li key={photo}>
                <img
                  src={photo}
                  alt={t('myPlaces.photoOf', {
                    n: at + 1,
                    count: place.photos.length,
                    name: place.name,
                  })}
                />
                <button
                  type="button"
                  className="button button-small button-quiet"
                  onClick={() => {
                    onRemovePhoto(photo);
                  }}
                >
                  {t('myPlaces.removePhoto')}
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="dialog-actions">
          <button type="button" className="button button-danger places-grow" onClick={onRemove}>
            {t('myPlaces.remove')}
          </button>
          <button type="button" className="button" onClick={onClose}>
            {t('myPlaces.cancel')}
          </button>
          <button type="submit" className="button button-primary">
            {t('myPlaces.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
